import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux';
import VerticalNavBar from '../../../components/Navbar/PlatformNavBar/VerticalNavBar'
import {
  getVideoBySerieId,
  getVideosInteractionByUser,
} from "../../../services/videos";
import CardBoxVideo2 from '../../../components/CardsBox/CardBoxVideo2';
import './SeriesDetailsPage.css';
import SeriesProgress from '../../../components/SeriesChallengeBox/SeriesProgress';
import CheckIcon from "@mui/icons-material/Check";
import { getSeriesById } from '../../../services/series';
import { checkUserMembership,checkAllowUser } from '../../../utils/checkSubscription';
import ChatComponent from '../../../components/ChatComponent/ChatComponent';

const SKELETON_STEPS = 4;
const HERO_IMAGE = '../../assets/images/sliders/sara-4.png';

const VideoStepSkeleton = ({ index, isLast }) => (
  <div className="step-container flex gap-4 justify-center items-center relative">
    <div
      className={`indicator rounded-full bg-so-300 w-10 h-10 hidden sm:flex justify-center items-center ${
        isLast ? 'last-element' : ''
      }`}
    >
      <p className="text-xl font-500 font-[DIN-Condensed]">{index + 1}</p>
    </div>
    <div className="series-video-skeleton w-full max-w-[520px] rounded-2xl p-4">
      <div className="flex flex-col md:flex-row gap-4 items-center">
        <div className="series-skeleton-block series-skeleton-thumbnail rounded-xl" />
        <div className="flex flex-col gap-3 w-full">
          <div className="series-skeleton-block series-skeleton-title rounded-full" />
          <div className="series-skeleton-block series-skeleton-subtitle rounded-full" />
          <div className="flex gap-2 pt-1">
            <div className="series-skeleton-block series-skeleton-chip rounded-full" />
            <div className="series-skeleton-block series-skeleton-chip rounded-full" />
            <div className="series-skeleton-block series-skeleton-chip rounded-full" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const SeriesProgressSkeleton = () => (
  <div className="series-progress-skeleton flex flex-col gap-5">
    <div className="series-skeleton-block series-skeleton-heading rounded-full" />
    <div className="series-skeleton-circle mx-auto rounded-full" />
    <div className="series-skeleton-block series-skeleton-bar rounded-full" />
    <div className="series-skeleton-block series-skeleton-copy rounded-full" />
    <div className="series-skeleton-block series-skeleton-copy short rounded-full" />
  </div>
);

const SeriesDetailsPage = () => {

  const [videosList, setVideosList] = useState([]);
  const [userVideosComplete, setUserVideosComplete] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccessLoading, setIsAccessLoading] = useState(true);
  const [serie,setSerie]=useState('');
  const [serieInfo,setSerieInfo] = useState(null);
  const currentUser = useSelector((state) => state.user.currentUser);
  const userId = currentUser?.username;
  const [allowUserList, setAllowUserList] = useState([]);
  const displayTitle = useMemo(() => {
    if (serieInfo?.title) {
      return serieInfo.title;
    }

    return serie ? String(serie).replaceAll('_', ' ') : 'Serie';
  }, [serie, serieInfo]);

  const displayDescription = useMemo(() => {
    if (serieInfo?.description) {
      return serieInfo.description;
    }

    return 'Estamos preparando tus clases y validando tu acceso para mostrarte el contenido disponible.';
  }, [serieInfo]);

  const showLoadingState = isLoading || isAccessLoading;

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const paramNames = Array.from(urlParams.keys());
    const value = urlParams.get(paramNames[0]) || '';
    let isMounted = true;

    const loadSeriesData = async () => {
      if (!value || !userId) {
        setSerie(value);
        setVideosList([]);
        setUserVideosComplete([]);
        setSerieInfo(null);
        setAllowUserList([]);
        setIsLoading(false);
        setIsAccessLoading(false);
        return;
      }

      setSerie(value);
      setIsLoading(true);
      setIsAccessLoading(true);

      try {
        const [seriesResponse, userInteractionResponse, videosResponse] = await Promise.all([
          getSeriesById(value),
          getVideosInteractionByUser(userId),
          getVideoBySerieId(value),
        ]);

        if (!isMounted) {
          return;
        }

        if (seriesResponse?.status === 404) {
          setSerieInfo(null);
        } else {
          setSerieInfo(seriesResponse?.data || null);
        }

        if (
          userInteractionResponse?.status === 404 ||
          userInteractionResponse?.response?.status === 404
        ) {
          setUserVideosComplete([]);
        } else {
          setUserVideosComplete(Array.isArray(userInteractionResponse) ? userInteractionResponse : []);
        }

        const sortedVideosList = Array.isArray(videosResponse?.data)
          ? [...videosResponse.data].sort((a, b) => a.episodeId - b.episodeId)
          : [];

        setVideosList(sortedVideosList);
      } catch (error) {
        console.log(error);
        if (isMounted) {
          setSerieInfo(null);
          setVideosList([]);
          setUserVideosComplete([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadSeriesData();

    return () => {
      isMounted = false;
    };
  }, [userId, currentUser]);


  useEffect(() => {
    const updateAccess = async () => {
      if (!videosList.length) {
        setAllowUserList([]);
        setIsAccessLoading(false);
        return;
      }

      try {
        setIsAccessLoading(true);
        const subscriptionList = await checkUserMembership(currentUser);
        const normalizedSubscriptionList = subscriptionList || [];

        const updatedAllowUserList = videosList.map((item) => {
          return checkAllowUser(item, currentUser, normalizedSubscriptionList);
        });

        setAllowUserList(updatedAllowUserList);
      } catch (error) {
        console.error(error);
      } finally {
        setIsAccessLoading(false);
      }
    };

    updateAccess();
  }, [videosList, currentUser]);


  return (
    <>
      <section id="series-details" className="index-platform flex">
        <VerticalNavBar />
        <div className="details-container w-full relative parallax">
          <div className="block w-full h-72 relative">
            <img
              className="object-cover image-detail w-full h-full"
              src={HERO_IMAGE}
              alt={displayTitle}
            />
            <div className="series-details-hero-overlay absolute inset-0 flex items-end">
              <div className="w-full px-6 py-8 md:px-10 md:py-10">
                {isLoading ? (
                  <div className="flex flex-col gap-4 max-w-2xl">
                    <div className="series-skeleton-block series-skeleton-hero-title rounded-full" />
                    <div className="series-skeleton-block series-skeleton-hero-copy rounded-full" />
                    <div className="series-skeleton-block series-skeleton-hero-copy short rounded-full" />
                  </div>
                ) : (
                  <div className="max-w-2xl text-white flex flex-col gap-3">
                    <span className="series-badge">Serie</span>
                    <h1 className="font-[DIN-Condensed] uppercase text-4xl md:text-5xl tracking-[0.12em]">
                      {displayTitle}
                    </h1>
                    <p className="text-sm md:text-base text-white/85 max-w-xl">
                      {displayDescription}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="details flex flex-wrap-reverse gap-5 lg:gap-0 justify-around py-10">
            <div className="flex flex-col gap-5">
              {showLoadingState ? (
                <>
                  <div className="series-loading-copy flex items-center gap-3 px-3">
                    <span className="series-loading-dot" />
                    <p className="font-[DIN-Condensed] uppercase tracking-[0.2em] text-sb-700 text-sm md:text-base">
                      Cargando contenido de la serie...
                    </p>
                  </div>
                  {Array.from({ length: SKELETON_STEPS }).map((_, index, arr) => (
                    <VideoStepSkeleton
                      key={`video-skeleton-${index}`}
                      index={index}
                      isLast={arr.length - 1 === index}
                    />
                  ))}
                </>
              ) : (!videosList.length ? (
                // If no videos are available, render the "no videos available" message
                <div className="flex flex-col justify-center items-center gap-5">
                    <div className="image-container flex flex-col justify center w-24">
                        <img src="../assets/icons/Forms/no-video.gif" alt="not-video" />
                    </div>
                    <h4 className="text-xl text-sb-800 opacity-50 uppercase font-[DIN-Condensed]">
                        No hay video disponibles
                    </h4>
                </div>
            ):
                videosList.map((item, index, arr) => {
                  var lastElement = false;
                  if (arr.length - 1 === index) {
                    lastElement = true;
                  }
                  return (
                    <div key={item.videoId}  className="step-container flex gap-4 justify-center items-center relative">
                      {userVideosComplete && userVideosComplete.some(
                        (videoUser) => videoUser.videoId === item.videoId
                      ) ? (
                        <div
                          className={`indicator-done rounded-full bg-sb-500 bg-  w-10 h-10 hidden sm:flex justify-center items-center ${
                            lastElement ? "last-element" : ""
                          }`}
                        >
                          <p className="text-xl font-500 font-[DIN-Condensed]">
                            <CheckIcon />
                          </p>
                        </div>
                      ) : (
                        <div
                          className={`indicator rounded-full bg-so-300  w-10 h-10 hidden sm:flex justify-center items-center ${
                            lastElement ? "last-element" : ""
                          }`}
                        >
                          <p className="text-xl font-500 font-[DIN-Condensed]">
                            {index + 1}
                          </p>
                        </div>
                      )}

                      <CardBoxVideo2
                        enable={currentUser?.email==='sara.asanas@gmail.com' ? true : Boolean(allowUserList[index])}
                        title={item.title}
                        duration={item.duration}
                        level={item.intensityLevel}
                        intensity={4}
                        path={item.thumbnail}
                        id={item.videoId}
                        imgUrl={item.thumbnail}
                        serie={item.tags?.[0] || ''}
                        loading={false}
                        key={item.videoId}
                        seriesInfo={serieInfo}
                      />
                    </div>
                  );
                }))
              }
            </div>
            <div className="progress-container flex flex-col gap-5 justify-center md:justify-start overeflow-auto">
              <div className="progress-box mx-auto w-10/12 sm:w-3/4 md:w-[300px] bg-sb-200 h-fit col-span-4 rounded-lg p-5 sticky top-0">
                {showLoadingState ? <SeriesProgressSkeleton /> : <SeriesProgress serie={serie} />}
                <div className="flex justify-center items-center w-full">
                  {showLoadingState ? (
                    <div className="series-chat-skeleton w-full mt-4 rounded-2xl p-4 flex flex-col gap-3">
                      <div className="series-skeleton-block series-skeleton-button rounded-full" />
                      <div className="series-skeleton-block series-skeleton-chat rounded-2xl" />
                    </div>
                  ) : (
                    <ChatComponent messageType={"CHALLENGE"} referenceId={serie}/>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default SeriesDetailsPage